import type {
  AggregateOffer,
  Product,
  ProductLeaf,
  PropertyValue,
  UnitPriceSpecification,
} from "@decocms/apps-commerce/types"

/**
 * Trim a schema.org Product to what a shelf/PLP ProductCard actually renders,
 * before it is serialized into the SSR/hydration payload.
 *
 * The VTEX `productListingPage` loader transforms every product with FULL
 * variants (it calls `toProduct` without the `leanVariants` option). Measured
 * on the real payload (`/deco/invoke` of the legacy PLP loader, /lingerie
 * count=36, 27/08/2026): **10.46 MB for one page**, 99.6% of it in `products`
 * — and inside each 289 KB product, 96% is `isVariantOf.hasVariant`:
 *   - 26 variants × 18.4 KB, each carrying the FULL 48-entry payment ladder
 *     (`priceSpecification`: 1x–10x for Visa/Master/Amex/Elo + Pix/Boleto/…)
 *     at 12.9 KB per SKU;
 *   - the same 3.4 KB `description` duplicated on every variant;
 *   - a NESTED `isVariantOf.hasVariant[]` on each variant (N² blow-up).
 *
 * What the listing UI actually reads (verified consumer sweep):
 *   - `ProductCard` displays exactly ONE SKU — the cheapest in-stock variant
 *     (`getMostCheapSKU`) → its offer (price/listPrice/installments/teasers)
 *     and its images (front + "vira" + "variacao");
 *   - every variant's `offers` price + availability (to pick that cheapest);
 *   - every variant's `additionalProperty` (to COUNT variant dimensions and
 *     decide "COMPRAR" vs add-to-cart — swatches are NOT rendered on the card);
 *   - the ROOT offer for GA4 (`toProductItem`: price/listPrice/teasers) and
 *     `inventoryLevel.value` (ProductCard's quantity selector cap).
 *
 * So we keep the cheapest variant full, reduce every other variant to
 * price + availability + additionalProperty, and cut the payment ladder down
 * to the three specs the code actually looks up (see {@link leanSpecs}).
 *
 * Shelf/PLP only — NEVER the PDP, which needs the full schema. Also never the
 * object PageSEO builds its JSON-LD from: bots still get full variants (that
 * copy is memo-shared, so callers must trim a COPY, never mutate in place).
 *
 * Idempotent: trimming an already-trimmed product is a no-op, so chaining
 * (loader → section → shelf) is safe.
 *
 * Known pre-existing quirk (NOT introduced here): `ProductCard` falls back to
 * the FIRST in-stock variant's offer when the cheapest lookup misses
 * (ProductCard.tsx:72-75). If those two ever diverge, installments render
 * empty — true before this trim too, since non-displayed variants never
 * carried a payment ladder.
 */

type Img = NonNullable<Product["image"]>[number]

const LIST_PRICE = "https://schema.org/ListPrice"
const SALE_PRICE = "https://schema.org/SalePrice"

// The card renders only 3 images from the displayed SKU: image[0] (front), the
// one named "vira" (hover) and the one named "variacao" (main override).
const keepCardImages = (images: Product["image"]): Product["image"] => {
  if (!images?.length) return images
  const [first] = images
  const named = images.filter(
    (i: Img) => (i.name?.toLowerCase() === "vira" || i.name?.toLowerCase() === "variacao") &&
      i !== first,
  )
  return [first, ...named]
}

/**
 * Cut the 48-entry payment ladder to the 3 specs consumers look up, using each
 * consumer's OWN predicate so the lookups keep resolving identically:
 *
 *   - `useOffer.ts:48` → `.find(priceType === ListPrice)` (list price strikethrough);
 *   - `PageSEO.tsx:255-272` (addPricePix) → `SalePrice` with no `name` (pix price);
 *   - `useOffer.ts:51-53` → `.filter(name === "Visa").reduce(bestInstallment)`, and
 *     that reducer returns `curr` for every Visa entry — i.e. the LAST Visa in
 *     the array, which is the highest installment count ("10x de R$ X"). Keeping
 *     exactly that entry preserves the rendered string.
 *
 * Everything else (Master/Amex/Elo/Boleto/Vale/Nubank/GPay ladders, SRP) is
 * never read on a shelf.
 */
export const leanSpecs = (
  specs?: UnitPriceSpecification[],
): UnitPriceSpecification[] | undefined => {
  if (!specs?.length) return specs
  const kept: UnitPriceSpecification[] = []

  const listPrice = specs.find((s) => s.priceType === LIST_PRICE)
  if (listPrice) kept.push(listPrice)

  const salePrice = specs.find((s) => s.priceType === SALE_PRICE && !s.name)
  if (salePrice) kept.push(salePrice)

  // Last Visa wins — mirrors `bestInstallment`'s early `return curr`.
  let visa: UnitPriceSpecification | undefined
  for (const s of specs) if (s.name === "Visa") visa = s
  if (visa) kept.push(visa)

  return kept.length === specs.length ? specs : kept
}

/** Displayed SKU / root offer: full shape, minus the unread payment ladder. */
const cardOffers = (offers?: AggregateOffer): AggregateOffer | undefined => {
  const o = offers?.offers?.[0]
  if (!offers || !o) return offers
  return { ...offers, offers: [{ ...o, priceSpecification: leanSpecs(o.priceSpecification) ?? [] }] }
}

/**
 * Non-displayed variant offer: only what `getMostCheapSKU` compares
 * (price + availability). No ladder, no teasers, no seller/inventory —
 * the card never shows this SKU.
 */
const minimalOffers = (offers?: AggregateOffer): AggregateOffer | undefined => {
  const o = offers?.offers?.[0]
  if (!offers || !o) return offers
  return {
    ...offers,
    offers: [
      {
        "@type": o["@type"],
        price: o.price,
        availability: o.availability,
        // Required by the `Offer` type and 2 numbers wide — keep it rather
        // than fake one.
        inventoryLevel: o.inventoryLevel,
        priceSpecification: [],
        teasers: [],
      },
    ],
  }
}

const priceOf = (v: ProductLeaf) => v.offers?.offers?.[0]?.price ?? 0
const isInStock = (v: ProductLeaf) =>
  v.offers?.offers?.[0]?.availability === "https://schema.org/InStock"

// Cheapest available variant — same selection the card makes via getMostCheapSKU.
export const cheapestVariantId = (variants: ProductLeaf[]): string | undefined => {
  const available = variants.filter(isInStock)
  if (!available.length) return undefined
  return available.reduce(
    (a, b) => (priceOf(b) < priceOf(a) || priceOf(a) === 0 ? b : a),
    available[0],
  ).productID
}

// The card reads variant `additionalProperty` only via useVariantPossibilities,
// which keys on name + value. Drop the spec bulk (propertyID, description,
// valueReference, unitCode) that VTEX ships on every entry.
const leanProps = (props?: PropertyValue[]): PropertyValue[] | undefined =>
  props?.map((p) => ({ "@type": "PropertyValue", name: p.name, value: p.value }))

const leanVariant = (v: ProductLeaf, keepFull: boolean): ProductLeaf => {
  // `isVariantOf`/`description`/`video` aren't on the ProductLeaf TYPE but VTEX
  // ships them at runtime — the nested variant tree is the N² blow-up. Strip
  // them by destructuring before the spread.
  const { isVariantOf: _nested, description: _d, video: _v, ...rest } = v as Product

  if (keepFull) {
    return {
      ...(rest as ProductLeaf),
      additionalProperty: leanProps(v.additionalProperty),
      image: keepCardImages(v.image),
      offers: cardOffers(v.offers),
    }
  }

  // Non-displayed variant: only the fields the cheapest-SKU pick and the
  // dimension COUNT need. `url` is dropped on purpose — useVariantPossibilities
  // stores it as a map VALUE (typed `string | undefined`) and the card reads
  // only `Object.keys(...)`, so nothing renders it.
  return {
    "@type": v["@type"],
    productID: v.productID,
    additionalProperty: leanProps(v.additionalProperty),
    offers: minimalOffers(v.offers),
  } as ProductLeaf
}

export function trimProductForShelf(product: Product): Product {
  const isVariantOf = product.isVariantOf
  const variants = isVariantOf?.hasVariant
  const keepId = variants ? cheapestVariantId(variants) : undefined
  return {
    ...product,
    description: undefined,
    video: undefined,
    image: keepCardImages(product.image),
    // Root offer stays full (GA4 + inventoryLevel read it) minus the ladder.
    offers: cardOffers(product.offers) ?? product.offers,
    // Top-level parent specs — unread by the card or GA4 (toProductItem reads
    // only brand/category/offers).
    additionalProperty: leanProps(product.additionalProperty),
    isVariantOf: isVariantOf && {
      ...isVariantOf,
      // The card reads isVariantOf.additionalProperty only for the "CountDown"
      // entry; keep name/value, drop the spec bulk.
      additionalProperty: leanProps(isVariantOf.additionalProperty) ??
        isVariantOf.additionalProperty,
      hasVariant: isVariantOf.hasVariant.map((v) => leanVariant(v, v.productID === keepId)),
    },
  }
}

export default trimProductForShelf
